import { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';

interface AnimatedCounterProps {
  value: string;
  duration?: number;
  className?: string;
}

const AnimatedCounter = ({ value, duration = 2000, className = '' }: AnimatedCounterProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });
  const [displayValue, setDisplayValue] = useState('0');

  useEffect(() => {
    if (!isInView) return;

    // Extract numeric value and suffix (like +, %, etc.)
    const match = value.match(/^([\d,]+\.?\d*)(.*)$/);
    if (!match) {
      setDisplayValue(value);
      return;
    }

    const numericPart = match[1].replace(/,/g, '');
    const suffix = match[2];
    const targetValue = parseFloat(numericPart);
    const hasDecimal = numericPart.includes('.');
    const decimalPlaces = hasDecimal ? numericPart.split('.')[1].length : 0;

    if (isNaN(targetValue)) {
      setDisplayValue(value);
      return;
    }

    const startTime = Date.now();
    const interval = 16; // ~60fps

    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out cubic function for smooth animation
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = targetValue * easeOut;
      
      // Format the value
      let formattedValue: string;
      if (hasDecimal) {
        formattedValue = currentValue.toFixed(decimalPlaces);
      } else if (match[1].includes(',')) {
        formattedValue = Math.floor(currentValue).toLocaleString();
      } else {
        formattedValue = Math.floor(currentValue).toString();
      }
      
      setDisplayValue(formattedValue + suffix);

      if (progress === 1) {
        clearInterval(timer);
        setDisplayValue(value); // Set to exact target value
      }
    }, interval);

    return () => clearInterval(timer);
  }, [isInView, value, duration]);

  return (
    <span ref={ref} className={className}>
      {displayValue}
    </span>
  );
};

export default AnimatedCounter;
